import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";

import missionControl from "../../img/missionControl.png";
import bhm from "../../img/bhm.png";
import ahnuld from "../../img/ahnuld.jpeg";
import ahnuld3 from "../../img/ahnuld_hq.gif";
import github from "../../img/github.png";
import github_hover from "../../img/github_hover.png";
import trianon from "../../img/trianon.png";
import cala from "../../img/cala.png";
import zemi from "../../img/zemi.png";

const Img = styled.img`
  width: 20px;
  padding-top: 10px;
  cursor: pointer;
  @media (max-width: 500px) {
    width: 25px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;

  a {
    display: block;
    max-width: 100%;
  }

  .portfolio-img-container {
    width: 550px;
    max-width: 100%;
    margin: 0 auto;

    @media (max-width: 800px) {
      width: 100%;
    }
  }

  button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    transition: all 0.2s ease-in-out;
    height: 70px;
    width: 40px;
    z-index: 1;

    svg {
      height: 100%;
      width: 100%;
      color: #e04343;
      transition: all 0.2s ease-in-out;
    }

    svg:hover {
      color: #e7e9d1;
      transition: all 0.2s ease-in-out;
    }
  }
`;

const DeskContrLeft = styled.button`
  left: calc(50% - 275px);
  opacity: 0.7;
  padding-left: 8px;

  @media (max-width: 500px) {
    display: none;
  }
`;

const DeskContrRight = styled.button`
  right: calc(50% - 275px);
  opacity: 0.7;
  padding-right: 8px;

  @media (max-width: 500px) {
    display: none;
  }
`;

const MobContr = styled.div`
  display: flex;
  width: 50%;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;

  button {
    opacity: 0.7;
    background: none;
    border: none;
    transition: all 0.2s ease-in-out;
    height: 50px;

    svg {
      height: 100%;
      color: #e7e9d1;
      transition: all 0.2s ease-in-out;
    }

    svg:hover {
      color: #e04343;
      transition: all 0.2s ease-in-out;
    }
  }

  @media (min-width: 500px) {
    display: none;
  }
`;

const LocInd = styled.div`
  opacity: 0.7;
  background: #e7e9d1;
  border-radius: 25px;
  height: 3px;
  width: 10px;
`;

function useCarousel(items) {
  const [index, setIndex] = useState(0);
  const [anim, setAnim] = useState(null);
  const ref = useRef(null);

  const next = useCallback(() => {
    setAnim("r");
    setTimeout(() => setAnim(null), 200);
    setIndex((i) => (i >= items.length - 1 ? 0 : i + 1));
  }, [items.length]);

  const prev = useCallback(() => {
    setAnim("l");
    setTimeout(() => setAnim(null), 200);
    setIndex((i) => (i > 0 ? i - 1 : items.length - 1));
  }, [items.length]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let xDown = null;
    let yDown = null;

    function handleTouchStart(evt) {
      const touch = (evt.touches || evt.originalEvent.touches)[0];
      xDown = touch.clientX;
      yDown = touch.clientY;
    }

    function handleTouchMove(evt) {
      if (!xDown || !yDown) return;
      const xDiff = xDown - evt.touches[0].clientX;
      const yDiff = yDown - evt.touches[0].clientY;
      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        xDiff > 0 ? next() : prev();
      }
      xDown = null;
      yDown = null;
    }

    el.addEventListener("touchstart", handleTouchStart, false);
    el.addEventListener("touchmove", handleTouchMove, false);
    return () => {
      el.removeEventListener("touchstart", handleTouchStart, false);
      el.removeEventListener("touchmove", handleTouchMove, false);
    };
  }, [next, prev]);

  return { index, anim, next, prev, ref };
}

const CarouselView = ({ items, carousel, renderImage, alt }) => (
  <>
    <CarouselWrapper>
      <DeskContrLeft onClick={carousel.prev}>
        <FaAngleLeft />
      </DeskContrLeft>
      {renderImage ? (
        renderImage(items[carousel.index], carousel.anim)
      ) : (
        <div className="portfolio-img-container">
          <img
            className={`${
              carousel.anim === "l"
                ? "transLeft"
                : carousel.anim === "r"
                ? "transRight"
                : ""
            } portfolio-img trans`}
            src={items[carousel.index].img}
            alt={alt}
          />
        </div>
      )}
      <DeskContrRight onClick={carousel.next}>
        <FaAngleRight />
      </DeskContrRight>
    </CarouselWrapper>
    <MobContr>
      <button onClick={carousel.prev}>
        <FaAngleLeft />
      </button>
      {items.map((item, i) => (
        <LocInd
          key={item.id}
          style={{ background: i === carousel.index ? "#e04343" : null }}
        />
      ))}
      <button onClick={carousel.next}>
        <FaAngleRight />
      </button>
    </MobContr>
  </>
);

const hiltonItems = [
  {
    id: 0,
    copy: "Cala San Miguel Ibiza - one of many resort hotel sites built with dynamic components in a shared monorepo",
    url: "https://www.hilton.com/en/hotels/ibzseqq-cala-san-miguel-ibiza-resort/",
    img: cala,
  },
  {
    id: 1,
    copy: "Waldorf Astoria Versailles, Trianon Palace - each component was designed to be functionally and stylistically flexible",
    url: "https://www.hilton.com/en/hotels/cdgtpwa-waldorf-astoria-versailles-trianon-palace/",
    img: trianon,
  },
  {
    id: 2,
    copy: "Zemi Beach House - all components are fully accessible and are used in more than 200 high-end resort sites",
    url: "https://www.hilton.com/en/hotels/axazbol-zemi-beach-house/",
    img: zemi,
  },
];

const ahnuldItems = [
  { id: 0, img: ahnuld3 },
  { id: 1, img: ahnuld },
];

const Portfolio = ({ portRef }) => {
  const [hover, setHover] = useState(false);
  const [hover3, setHover3] = useState(false);
  const [hover4, setHover4] = useState(false);

  const hiltonCarousel = useCarousel(hiltonItems);
  const ahnuldCarousel = useCarousel(ahnuldItems);

  return (
    <div ref={portRef} className="portfolio-section">
      <div className="portfolio-piece">
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://youtu.be/IE5NFcyit98"
        >
          <div className="portfolio-img-container">
            <img className="portfolio-img" src={bhm} alt="BH&M Screenshot"></img>
          </div>
        </a>
        <div className="text-box">
          <h4>BH&M Dashboard</h4>
          <h3>
            Internal business tool for the company that provides
            environmental services to the largest full-service utility company
            in the US.
            <br />
            <a
              target="blank"
              rel="noopener noreferrer"
              href="https://github.com/Hammurobbie"
            >
              <Img
                alt="github link"
                onMouseEnter={() => setHover(true)}
                onMouseOut={() => setHover(false)}
                src={hover ? github_hover : github}
              ></Img>
            </a>
          </h3>
        </div>
      </div>
      <div ref={hiltonCarousel.ref} className="portfolio-piece">
        <CarouselView
          items={hiltonItems}
          carousel={hiltonCarousel}
          alt="hilton screenshot"
          renderImage={(item, anim) => (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={item.url}
            >
              <div className="portfolio-img-container">
                <img
                  className={`${
                    anim === "l" ? "transLeft" : anim === "r" ? "transRight" : ""
                  } portfolio-img trans`}
                  src={item.img}
                  alt="hilton screenshot"
                />
              </div>
            </a>
          )}
        />
        <div className="text-box">
          <h4>Hilton Resorts</h4>
          <h3 className={hiltonCarousel.anim ? "transBlur" : "trans"}>
            {hiltonItems[hiltonCarousel.index].copy}
            <br />
          </h3>
          <a
            target="blank"
            rel="noopener noreferrer"
            href="https://github.com/Hammurobbie/"
          >
            <Img
              alt="github link"
              onMouseEnter={() => setHover4(true)}
              onMouseOut={() => setHover4(false)}
              src={hover4 ? github_hover : github}
            ></Img>
          </a>
        </div>
      </div>
      <div ref={ahnuldCarousel.ref} className="portfolio-piece">
        <CarouselView
          items={ahnuldItems}
          carousel={ahnuldCarousel}
          alt="ahnuld"
        />
        <div className="text-box">
          <h4>Ahnuld Bot</h4>
          <h3>
            3D modeled/printed, voice-controlled agentic Raspberry Pi utilizing LLM, TTS, STT and Face Recognition models for home control.
            <br />
            <a
              target="blank"
              rel="noopener noreferrer"
              href="https://github.com/Hammurobbie/ahnuld"
            >
              <Img
                alt="github link"
                onMouseEnter={() => setHover3(true)}
                onMouseOut={() => setHover3(false)}
                src={hover3 ? github_hover : github}
              ></Img>
            </a>
          </h3>
        </div>
      </div>
      <div className="portfolio-piece">
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://youtu.be/daNBSogBkac?t=11066"
        >
          <div className="portfolio-img-container">
            <img
              className="portfolio-img"
              src={missionControl}
              alt="Mission Control Screenshot"
            ></img>
          </div>
        </a>
        <div className="text-box">
          <h4>Mission Control</h4>
          <h3>
            A program management tool developed with a cross-functional team to
            be used by Lambda School and other organizations
            <br />
            <a
              target="blank"
              rel="noopener noreferrer"
              href="https://github.com/Hammurobbie/mission-control-fe"
            >
              <Img
                alt="github link"
                onMouseEnter={() => setHover3(true)}
                onMouseOut={() => setHover3(false)}
                src={hover3 ? github_hover : github}
              ></Img>
            </a>
          </h3>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
